import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, DollarSign, Globe, MapPin, Mail, Phone, User, Award, FileText, Link, Building, Megaphone, ExternalLink } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import GallerySelector from './GallerySelector';
import UserSelector from './UserSelector';
import '../../styles/OpenCallsAdmin.css';

const ExternalCalloutPage = () => {
  const navigate = useNavigate();
  const { calloutId } = useParams();
  const isEditing = !!calloutId;

  const [formData, setFormData] = useState({
    title: '',
    callout_headline: '',
    submission_deadline: '',
    open_date: new Date().toISOString().split('T')[0],
    external_url: '',
    source_type: 'external_managed',
    source_url: '',
    
    // Contact info (for external contacts not in our system)
    contact_email: '',
    contact_phone: '',
    sponsor_name: '',
    
    // Venue and scope
    scope: '',
    
    // Details
    awards: '',
    entry_fee_description: '',
    submission_price: '',
    eligible_media: '',
    juror_name: '',
    submission_guidelines: '',
    
    // Settings
    featured: false,
    status: 'draft',
    approval_status: 'approved',
    cover_image: ''
  });

  // Separate state for gallery selection
  const [selectedGallery, setSelectedGallery] = useState(null);
  
  // Separate state for user selection (submitted by)
  const [submittedByUser, setSubmittedByUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [errors, setErrors] = useState({});

  // Load existing callout data if editing
  useEffect(() => {
    if (isEditing) {
      loadCallout();
    }
  }, [calloutId]);

  const loadCallout = async () => {
    try {
      setLoadingData(true);
      const { data, error } = await supabase
        .from('submissions_details')
        .select('*, galleries(gallery_id, name, address)')
        .eq('submission_id', calloutId)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          title: data.title || '',
          callout_headline: data.callout_headline || '',
          submission_deadline: data.submission_deadline ? data.submission_deadline.split('T')[0] : '',
          open_date: data.open_date ? data.open_date.split('T')[0] : '',
          external_url: data.external_url || '',
          source_type: data.source_type || 'external_managed',
          source_url: data.source_url || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          sponsor_name: data.sponsor_name || '',
          scope: data.scope || '',
          awards: data.awards || '',
          entry_fee_description: data.entry_fee_description || '',
          submission_price: data.submission_price || '',
          eligible_media: data.eligible_media || '',
          juror_name: data.juror_name || '',
          submission_guidelines: data.submission_guidelines || '',
          featured: data.featured || false,
          status: data.status || 'draft',
          approval_status: data.approval_status || 'approved',
          cover_image: data.cover_image || ''
        });

        // Load gallery info if linked
        if (data.galleries) {
          setSelectedGallery({
            gallery_id: data.galleries.gallery_id,
            name: data.galleries.name,
            address: data.galleries.address,
            isNew: false
          });
        } else if (data.venue_location) {
          // Legacy: venue_location without gallery_id
          setSelectedGallery({
            gallery_id: null,
            name: data.venue_location,
            address: data.venue_location,
            isNew: false
          });
        }
        
        // Load submitted by user separately (no FK relationship in schema)
        if (data.submitted_by_user_id) {
          const { data: userData } = await supabase
            .from('users')
            .select('uuid, email, first_name, last_name, handle, profile_img')
            .eq('uuid', data.submitted_by_user_id)
            .single();

          if (userData) {
            setSubmittedByUser({
              uuid: userData.uuid,
              email: userData.email,
              first_name: userData.first_name,
              last_name: userData.last_name,
              handle: userData.handle,
              profile_img: userData.profile_img
            });
          }
        } else if (data.submitted_by_email) {
          // Legacy: email without user link - show as text
          setSubmittedByUser({
            uuid: null,
            email: data.submitted_by_email,
            first_name: null,
            last_name: null
          });
        }
      }
    } catch (error) {
      console.error('Error loading callout:', error);
      alert('Failed to load callout data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.external_url.trim()) newErrors.external_url = 'External URL is required for external callouts';
    if (!formData.submission_deadline) newErrors.submission_deadline = 'Deadline is required';
    
    // Validate URL format
    if (formData.external_url && !isValidUrl(formData.external_url)) {
      newErrors.external_url = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      const submissionData = {
        title: formData.title,
        callout_headline: formData.callout_headline || formData.title,
        submission_deadline: formData.submission_deadline ? new Date(formData.submission_deadline).toISOString() : null,
        open_date: formData.open_date ? new Date(formData.open_date).toISOString() : new Date().toISOString(),
        external_url: formData.external_url,
        source_type: formData.source_type,
        source_url: formData.source_url || formData.external_url,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        sponsor_name: formData.sponsor_name || null,
        venue_location: selectedGallery?.address || null,
        gallery_id: selectedGallery?.gallery_id || null,
        // Link to user if selected
        submitted_by_user_id: submittedByUser?.uuid || null,
        submitted_by_email: submittedByUser?.email || formData.contact_email || null,
        scope: formData.scope || null,
        awards: formData.awards || null,
        entry_fee_description: formData.entry_fee_description || null,
        submission_price: formData.submission_price ? parseFloat(formData.submission_price) : null,
        eligible_media: formData.eligible_media || null,
        juror_name: formData.juror_name || null,
        submission_guidelines: formData.submission_guidelines || null,
        featured: formData.featured,
        status: formData.status,
        approval_status: formData.approval_status,
        cover_image: formData.cover_image || null
      };

      if (isEditing) {
        const { error } = await supabase
          .from('submissions_details')
          .update(submissionData)
          .eq('submission_id', calloutId);

        if (error) throw error;
      } else {
        submissionData.created_at = new Date().toISOString();
        
        const { error } = await supabase
          .from('submissions_details')
          .insert([submissionData]);

        if (error) throw error;
      }

      navigate('/admin/open-calls');
    } catch (error) {
      console.error('Error saving callout:', error);
      alert('Failed to save callout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="open-calls-admin-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading callout data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="open-calls-admin-container">
      <div className="open-calls-admin-content">
        {/* Header */}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate('/admin/open-calls')}>
            <ArrowLeft size={20} />
            Back to Open Calls
          </button>
          <div className="header-title">
            <Megaphone className="header-icon" size={28} />
            <div>
              <h1>{isEditing ? 'Edit External Callout' : 'Add External Callout'}</h1>
              <p>Create a listing for an external open call opportunity</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="callout-form">
          {/* Basic Information */}
          <div className="form-card">
            <h3 className="card-title">Basic Information</h3>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label>
                  <FileText size={14} />
                  Call Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Annual Juried Exhibition 2026"
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>

              <div className="form-group full-width">
                <label>
                  <FileText size={14} />
                  Headline / Short Description
                </label>
                <input
                  type="text"
                  name="callout_headline"
                  value={formData.callout_headline}
                  onChange={handleInputChange}
                  placeholder="A brief description for the listing card"
                />
              </div>

              <div className="form-group">
                <label>
                  <Link size={14} />
                  External URL *
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <input
                    type="url"
                    name="external_url"
                    value={formData.external_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/apply"
                    className={errors.external_url ? 'error' : ''}
                    style={{ flex: 1 }}
                  />
                  {formData.external_url && isValidUrl(formData.external_url) && (
                    <a
                      href={formData.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        whiteSpace: 'nowrap',
                        padding: '0.5rem 0.75rem',
                        textDecoration: 'none'
                      }}
                    >
                      <ExternalLink size={14} />
                      Check URL
                    </a>
                  )}
                </div>
                {errors.external_url && <span className="error-text">{errors.external_url}</span>}
              </div>

              <div className="form-group">
                <label>
                  <Globe size={14} />
                  Source Type
                </label>
                <select
                  name="source_type"
                  value={formData.source_type}
                  onChange={handleInputChange}
                >
                  <option value="external_managed">External Managed</option>
                  <option value="external_scraped">External Scraped</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="form-card">
            <h3 className="card-title">Dates</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <Calendar size={14} />
                  Opens On
                </label>
                <input
                  type="date"
                  name="open_date"
                  value={formData.open_date}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>
                  <Calendar size={14} />
                  Submission Deadline *
                </label>
                <input
                  type="date"
                  name="submission_deadline"
                  value={formData.submission_deadline}
                  onChange={handleInputChange}
                  className={errors.submission_deadline ? 'error' : ''}
                />
                {errors.submission_deadline && <span className="error-text">{errors.submission_deadline}</span>}
              </div>
            </div>
          </div>

          {/* Organization & Contact */}
          <div className="form-card">
            <h3 className="card-title">Organization & Contact</h3>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label>
                  <MapPin size={14} />
                  Venue / Gallery
                </label>
                <GallerySelector
                  value={selectedGallery}
                  onChange={setSelectedGallery}
                  placeholder="Search existing galleries or add new venue..."
                />
                <span className="field-hint">
                  Search our gallery database or use Google Places to find and add a new venue
                </span>
              </div>

              <div className="form-group full-width">
                <label>
                  <User size={14} />
                  Submitted By (User)
                </label>
                <UserSelector
                  value={submittedByUser}
                  onChange={setSubmittedByUser}
                  placeholder="Search users by name, email, or handle..."
                />
                <span className="field-hint">
                  Link to an existing user in our system to avoid duplicates
                </span>
              </div>

              <div className="form-group">
                <label>
                  <User size={14} />
                  Sponsor Name
                </label>
                <input
                  type="text"
                  name="sponsor_name"
                  value={formData.sponsor_name}
                  onChange={handleInputChange}
                  placeholder="Sponsor or host (if different from user)"
                />
              </div>

              <div className="form-group">
                <label>
                  <Mail size={14} />
                  Contact Email
                </label>
                <input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  placeholder="contact@example.com"
                />
                <span className="field-hint">
                  For external contacts not in our system
                </span>
              </div>

              <div className="form-group">
                <label>
                  <Phone size={14} />
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Call Details */}
          <div className="form-card">
            <h3 className="card-title">Call Details</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <Globe size={14} />
                  Scope
                </label>
                <select
                  name="scope"
                  value={formData.scope}
                  onChange={handleInputChange}
                >
                  <option value="">Select scope...</option>
                  <option value="National">National</option>
                  <option value="International">International</option>
                  <option value="Regional">Regional</option>
                  <option value="Local">Local</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <DollarSign size={14} />
                  Entry Fee
                </label>
                <input
                  type="text"
                  name="entry_fee_description"
                  value={formData.entry_fee_description}
                  onChange={handleInputChange}
                  placeholder="e.g., $35 for up to 3 pieces"
                />
              </div>

              <div className="form-group">
                <label>
                  <DollarSign size={14} />
                  Fee Amount ($)
                </label>
                <input
                  type="number"
                  name="submission_price"
                  value={formData.submission_price}
                  onChange={handleInputChange}
                  placeholder="35"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>
                  <User size={14} />
                  Juror Name
                </label>
                <input
                  type="text"
                  name="juror_name"
                  value={formData.juror_name}
                  onChange={handleInputChange}
                  placeholder="Juror or judge"
                />
              </div>

              <div className="form-group full-width">
                <label>
                  <Award size={14} />
                  Awards
                </label>
                <textarea
                  name="awards"
                  value={formData.awards}
                  onChange={handleInputChange}
                  placeholder="e.g., Best in Show: $1,000, Merit Awards: $500"
                  rows={2}
                />
              </div>

              <div className="form-group full-width">
                <label>
                  <FileText size={14} />
                  Eligible Media
                </label>
                <input
                  type="text"
                  name="eligible_media"
                  value={formData.eligible_media}
                  onChange={handleInputChange}
                  placeholder="e.g., All 2D media, Photography, Sculpture"
                />
              </div>

              <div className="form-group full-width">
                <label>
                  <FileText size={14} />
                  Submission Guidelines
                </label>
                <textarea
                  name="submission_guidelines"
                  value={formData.submission_guidelines}
                  onChange={handleInputChange}
                  placeholder="Any additional guidelines or requirements..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="form-card">
            <h3 className="card-title">Settings</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="draft">Draft</option>
                  <option value="live">Live</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Approval</label>
                <select
                  name="approval_status"
                  value={formData.approval_status}
                  onChange={handleInputChange}
                >
                  <option value="approved">Approved</option>
                  <option value="pending">Pending Review</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>
                  <Link size={14} />
                  Cover Image URL
                </label>
                <input
                  type="url"
                  name="cover_image"
                  value={formData.cover_image}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span>Featured Callout</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate('/admin/open-calls')}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <>Saving...</>
              ) : (
                <>
                  <Save size={16} />
                  {isEditing ? 'Update Callout' : 'Create Callout'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExternalCalloutPage;
